import React, { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { QuoteResponse } from "../interfaces/QuoteResponse";
import GuardLogo from "../images/holdingCar_v1_red.svg";
import ProtectLogo from "../images/holdingCar_v1_grey.svg";
import ProtectPlusLogo from "../images/holdingCar_v1_gold.svg";
import { ProductType } from "../enums/ProductType";
import CustomDivider from "./CustomDivider";
import CustomButton from "./CustomButton";
import Alert from "./Alert";
import { acceptVapSureQuote } from "../api/QuoteApi";

type ProductBoxType = {
  quote: QuoteResponse | undefined;
  customerId: string | undefined;
};

const ProductBox = ({ quote, customerId }: ProductBoxType) => {
  const navigate = useNavigate();
  const offer = quote?.offer;

  const acceptVapSureQuoteMutation = useMutation({
    mutationFn: () => {
      const { externalId, request } = quote!;
      const { policyHolder } = request;
      return acceptVapSureQuote(externalId, offer!.product.quoteNumber, {
        groupId: offer!.product.vapSureId ?? "",
        registrationNumber: "",
        contactNumber: policyHolder.cellNumber,
        emailAddress: policyHolder.email,
        firstName: policyHolder.firstname,
        lastName: policyHolder.surname,
        idNumber: policyHolder.idNumber,
      });
    },
    onSuccess: () => {
      navigate("/quote/confirmation", {
        replace: true,
        state: { customerId },
      });
    },
  });

  const getLogo = (product: string | undefined) => {
    switch (product) {
      case ProductType.GUARD:
        return GuardLogo;
      case ProductType.PROTECTOR:
        return ProtectLogo;
      case ProductType.GUARDIAN:
        return ProtectPlusLogo;

      default:
        break;
    }
  };

  const getColor = (product: string | undefined) => {
    switch (product) {
      case ProductType.GUARD:
        return "border-accent";
      case ProductType.PROTECTOR:
        return "border-grayProduct";
      case ProductType.GUARDIAN:
        return "border-goldProduct";

      default:
        break;
    }
  };

  const isProtector = offer?.product.productName === ProductType.PROTECTOR;

  const showButton = (product: string | undefined) => {
    switch (product) {
      case ProductType.GUARD:
        return false;
      case ProductType.PROTECTOR:
        return true;
      case ProductType.GUARDIAN:
        return true;

      default:
        break;
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        tabIndex={0}
        className={`collapse collapse-arrow rounded-none bg-white shadow-elevationLight border-l-4 ${getColor(
          offer?.product.productName,
        )}`}
      >
        <div className="collapse-title text-xl font-medium pr-4">
          <div className="flex">
            <img
              src={getLogo(offer?.product.productName)}
              className="h-8"
              alt="guard-logo"
            ></img>
            <h1 className="font-archivo font-extrabold text-3xl ml-2">
              {offer?.product.productName}
            </h1>
          </div>
          <div className="flex justify-between items-center gap-4 mt-3">
            <h2 className="font-archivo text-base">
              R {offer?.product.premiumTotal.amount}
            </h2>
            {showButton(offer?.product.productName) && (
              <div className="w-32 shrink-0">
                <CustomButton
                  onClick={() => acceptVapSureQuoteMutation.mutate()}
                  disabled={acceptVapSureQuoteMutation.isPending || isProtector}
                >
                  {acceptVapSureQuoteMutation.isPending
                    ? "Sending..."
                    : "Call Back"}
                </CustomButton>
              </div>
            )}
          </div>
        </div>
        <div className="collapse-content flex flex-col gap-4">
          <CustomDivider className="before:bg-accent after:bg-accent" />
          <Info title="Cover Type">{offer?.product.coverType}</Info>
          <Info title="3rd Party Liability">{isProtector ? "Not Available" : "R 2.5 Million"}</Info>
          <Info title="Car Hire">{offer?.product.carHire}</Info>
          <Info title="Insured Value">{offer?.product.insuredValue}</Info>
          <Info title="Basic Excess">
            {offer?.product.basicExcessDescription ??
              `R ${offer?.product.excess}`}
          </Info>
          <Info title="SASRIA">{isProtector ? "Not Available" : "R 2.02"}</Info>
        </div>
      </div>
      {acceptVapSureQuoteMutation.isError && (
        <Alert
          text="We could not submit your call back request. Please try again, or talk to your salesman!"
          onClose={() => acceptVapSureQuoteMutation.reset()}
        />
      )}
    </div>
  );
};

const Info = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <div className="flex justify-between">
      <div className="flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
          stroke="currentColor"
          className="size-5 text-accent"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m8.25 4.5 7.5 7.5-7.5 7.5"
          />
        </svg>
        <div className="font-semibold leading-none ml-1">{title}</div>
      </div>
      <div className="leading-none text-right">{children}</div>
    </div>
  );
};

export default ProductBox;
